import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown, SupTitle } from "components";
import { PipelineRoutingLinkButton } from "components/Molecules/PipelineRoutingLinkButton";
import { FullArrow } from "icons";
import { Hero11Content } from "types/graphql/generated";
interface Props {
  content: Hero11Content;
}

const Hero11: VFC<Props> = withErrorBoundary(
  ({
    content: {
      hero11PageTitle,
      hero11BubbleEyeBrow,
      title,
      description,
      buttonLabel,
      buttonLink,
      image: {
        data: { image },
      },
    },
  }) => {
    return (
      <StyledHero11>
        <div className="Hero11">
          <div className="Hero11__container Content">
            <div className="Hero11__mainContent">
              {hero11PageTitle && (
                <SupTitle color="Color_secondary" className="Font_18_26_700 ">
                  {hero11PageTitle}
                </SupTitle>
              )}
              {hero11BubbleEyeBrow && (
                <Markdown className="Hero11__bubbleEyeBrow Font_18_26_700">{hero11BubbleEyeBrow}</Markdown>
              )}
              <Markdown className="Hero11__title Font_80_88_600 lg:Font_64_72_600 sm:Font_48_54_600 Color_primary">
                {title}
              </Markdown>
              <Markdown className="Hero11__description Font_24_32_450 Color_secondary">{description}</Markdown>
              <PipelineRoutingLinkButton rightIcon={<FullArrow />} href={buttonLink} className="Hero11__button">
                {buttonLabel}
              </PipelineRoutingLinkButton>
            </div>
            <LegacyImage {...image} layout="fill" objectFit="cover" className="Hero11__image" />
          </div>
        </div>
      </StyledHero11>
    );
  },
  {}
);

const StyledHero11 = styled.section`
  .TestImage {
    background: ${({ theme }) => theme.colors.fog["30"]};
    max-width: 529px;
    width: 100%;
    height: 600px;
  }

  .TestImageMobile {
    display: none;
  }

  .Hero11 {
    padding: 40px 0;
    background-color: ${({ theme }) => theme.colors.coral["05"]};
  }

  .Hero11__container {
    display: flex;
    justify-content: space-between;
  }

  .Hero11__mainContent {
    max-width: 586px;
    margin-top: 64px;
  }

  .Hero11__bubbleEyeBrow {
    width: fit-content;
    padding: 8px 24px;
    border: 1.5px solid ${({ theme }) => theme.colors.dusk["70"]};
    border-radius: 48px;
    color: ${({ theme }) => theme.colors.dusk["70"]};
  }

  .Hero11__title {
    margin-top: 32px;
  }

  .Hero11__description {
    margin-top: 24px;
  }

  .Hero11__button {
    margin-top: 56px;
    width: fit-content;
  }

  .Hero11__image {
    position: relative;
    margin-left: 32px;
    max-width: 529px;
    width: 100%;
    height: 600px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Hero11 {
      flex-direction: column;
      padding: 64px 0 80px;
    }

    .Hero11__mainContent {
      margin-top: 0;
    }

    .Hero11__image {
      display: none;
    }

    .TestImageMobile {
      display: block;
      max-width: unset;
      height: 441px;
      margin-left: unset;
      background: ${({ theme }) => theme.colors.fog["30"]};
      margin-top: -167px;
    }

    .Hero11__description {
      max-width: 520px;
    }

    .Hero11__form {
      margin-top: 40px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Hero11__mainContent {
      max-width: 466px;
    }

    /* .TestImageMobile {
      display: none;
    } */

    .Hero11 {
      padding-bottom: 80px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Hero11 {
      padding: 40px 0 64px;
    }

    .Hero11__description {
      margin-top: 16px;
    }

    .Hero11__button {
      margin-top: 40px;
    }
  }
`;

gql`
  fragment Hero11Content on ComponentHeroHero11 {
    hero11PageTitle
    hero11BubbleEyeBrow
    title
    description
    buttonLabel
    buttonLink
    image {
      data {
        image: attributes {
          src: hash
          width
          height
          alt: alternativeText
        }
      }
    }
  }
`;

export { Hero11 };
export type { Props as Hero11Props };
